import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  WireframeLayout,
  WireframeButton,
  CrisisBadge,
} from './WireframeLayout';
import { Layers, Download, Search, ChevronDown, X, FileText, Zap, Calendar, Play, Pause, ChevronLeft, ChevronRight, Database } from 'lucide-react';
import { cn } from '../../lib/utils';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { TutorialStartButton } from './WireframeTutorial';
import { getMockEWASRecords, mockACLEDEvents, mockIOMDTMRecords, mockFEWSNETRecords } from '../../data/ewasData';
import { EWASSourceId } from '../../services/nrc-ewas/types';

interface CrisisMarker {
  id: string;
  name: string;
  type: 'cholera' | 'flooding' | 'drought' | 'team' | 'conflict' | 'displacement' | 'food_insecurity';
  coordinates: [number, number];
  severity?: 'critical' | 'high' | 'medium';
  cases?: number;
  region?: string;
  status?: string;
  members?: number;
  affectedPopulation?: number;
  description?: string;
}

// Country and region data
const countryData: Record<string, { center: [number, number]; zoom: number; regions: string[] }> = {
  'Sudan': {
    center: [30.0, 15.5],
    zoom: 5,
    regions: ['All Regions', 'Khartoum', 'North Darfur', 'South Darfur', 'West Darfur', 'Central Darfur', 'East Darfur', 'Blue Nile', 'White Nile', 'Red Sea', 'Kassala', 'Gedaref', 'Sennar', 'North Kordofan', 'South Kordofan']
  },
  'Ethiopia': {
    center: [40.5, 8.5],
    zoom: 5,
    regions: ['All Regions', 'Addis Ababa', 'Amhara', 'Oromia', 'Somali', 'Tigray', 'Afar', 'SNNPR', 'Benishangul-Gumuz', 'Gambela', 'Sidama']
  },
  'South Sudan': {
    center: [31.0, 7.0],
    zoom: 5.5,
    regions: ['All Regions', 'Central Equatoria', 'Eastern Equatoria', 'Western Equatoria', 'Jonglei', 'Unity', 'Upper Nile', 'Lakes', 'Warrap', 'Western Bahr el Ghazal', 'Northern Bahr el Ghazal']
  },
  'Somalia': {
    center: [46.0, 5.0],
    zoom: 5,
    regions: ['All Regions', 'Banadir', 'Bay', 'Gedo', 'Hiiraan', 'Lower Juba', 'Middle Juba', 'Lower Shabelle', 'Middle Shabelle', 'Mudug', 'Nugaal', 'Puntland', 'Somaliland']
  },
  'Yemen': {
    center: [48.0, 15.5],
    zoom: 5.5,
    regions: ['All Regions', "Sana'a", 'Aden', 'Taiz', 'Hodeidah', 'Marib', 'Hajjah', 'Saada', 'Ibb', 'Lahj', 'Shabwah', 'Hadramaut']
  }
};

const countries = Object.keys(countryData);

// Sudan-focused crisis markers
const sudanCrisisMarkers: CrisisMarker[] = [
  // Conflict zones
  { id: 'conflict-1', name: 'Active Conflict - Khartoum', type: 'conflict', coordinates: [32.53, 15.50], severity: 'critical', affectedPopulation: 2500000, region: 'Khartoum', description: 'Ongoing armed conflict in capital city' },
  { id: 'conflict-2', name: 'Armed Clashes - El Fasher', type: 'conflict', coordinates: [25.35, 13.63], severity: 'critical', affectedPopulation: 450000, region: 'North Darfur', description: 'Intense fighting in regional capital' },
  { id: 'conflict-3', name: 'Military Operations - Nyala', type: 'conflict', coordinates: [24.88, 12.05], severity: 'high', affectedPopulation: 320000, region: 'South Darfur', description: 'Military operations affecting civilian areas' },

  // Displacement
  { id: 'displacement-1', name: 'IDP Camp - Zalingei', type: 'displacement', coordinates: [23.47, 12.91], severity: 'critical', affectedPopulation: 180000, region: 'Central Darfur', description: 'Large-scale displacement camp' },
  { id: 'displacement-2', name: 'Refugee Movement - Kassala', type: 'displacement', coordinates: [36.40, 15.45], severity: 'high', affectedPopulation: 95000, region: 'Kassala', description: 'Cross-border refugee influx' },
  { id: 'displacement-3', name: 'IDP Settlement - Gedaref', type: 'displacement', coordinates: [35.40, 14.03], severity: 'high', affectedPopulation: 72000, region: 'Gedaref', description: 'New IDP settlements forming' },

  // Food insecurity
  { id: 'food-1', name: 'Famine Risk - West Darfur', type: 'food_insecurity', coordinates: [22.45, 13.45], severity: 'critical', affectedPopulation: 890000, region: 'West Darfur', description: 'IPC Phase 5 - Famine conditions' },
  { id: 'food-2', name: 'Food Crisis - South Kordofan', type: 'food_insecurity', coordinates: [29.42, 11.20], severity: 'critical', affectedPopulation: 650000, region: 'South Kordofan', description: 'IPC Phase 4 - Emergency' },
  { id: 'food-3', name: 'Acute Hunger - Blue Nile', type: 'food_insecurity', coordinates: [34.35, 11.77], severity: 'high', affectedPopulation: 280000, region: 'Blue Nile', description: 'Severe food shortages' },

  // Health emergencies
  { id: 'cholera-1', name: 'Cholera Outbreak - Port Sudan', type: 'cholera', coordinates: [37.22, 19.62], severity: 'critical', cases: 1847, region: 'Red Sea', description: 'Active cholera outbreak' },
  { id: 'cholera-2', name: 'Disease Spread - Omdurman', type: 'cholera', coordinates: [32.48, 15.65], severity: 'high', cases: 423, region: 'Khartoum', description: 'Suspected cholera cases' },

  // Flooding
  { id: 'flooding-1', name: 'Flood Risk - White Nile', type: 'flooding', coordinates: [32.50, 13.16], severity: 'medium', region: 'White Nile', description: 'Seasonal flooding expected' },
  { id: 'flooding-2', name: 'Flash Floods - Sennar', type: 'flooding', coordinates: [33.62, 13.55], severity: 'high', region: 'Sennar', description: 'Recent flash flooding damage' },

  // Response teams
  { id: 'team-1', name: 'UNHCR Emergency Team', type: 'team', coordinates: [36.20, 15.30], status: 'Active', members: 12, region: 'Kassala' },
  { id: 'team-2', name: 'WFP Distribution Team', type: 'team', coordinates: [23.00, 13.20], status: 'Active', members: 8, region: 'West Darfur' },
  { id: 'team-3', name: 'MSF Medical Unit', type: 'team', coordinates: [37.00, 19.50], status: 'Active', members: 15, region: 'Red Sea' },
  { id: 'team-4', name: 'ICRC Assessment Team', type: 'team', coordinates: [32.00, 15.20], status: 'En Route', members: 6, region: 'Khartoum' },
];

const ethiopiaCrisisMarkers: CrisisMarker[] = [
  { id: 'cholera-1', name: 'Cholera Outbreak - Jijiga', type: 'cholera', coordinates: [42.79, 9.35], severity: 'critical', cases: 247, region: 'Somali' },
  { id: 'cholera-2', name: 'Cholera Spread - Kebridehar', type: 'cholera', coordinates: [44.2, 6.73], severity: 'critical', cases: 89, region: 'Somali' },
  { id: 'flooding-1', name: 'Flood Risk - Oromia', type: 'flooding', coordinates: [39.76, 7.0], severity: 'high', region: 'Oromia' },
  { id: 'drought-1', name: 'Drought Zone - Afar', type: 'drought', coordinates: [40.0, 11.5], severity: 'medium', region: 'Afar' },
  { id: 'team-1', name: 'WASH Team Alpha', type: 'team', coordinates: [42.5, 9.2], status: 'Active', members: 6, region: 'Somali' },
  { id: 'team-2', name: 'Health Team Bravo', type: 'team', coordinates: [44.0, 6.5], status: 'Active', members: 5, region: 'Somali' },
];

// Crisis layers (CLEAR data)
const layers = [
  { id: 'conflict', label: 'Active Conflict', color: 'bg-red-800', defaultChecked: true },
  { id: 'displacement', label: 'Displacement', color: 'bg-purple-600', defaultChecked: true },
  { id: 'food_insecurity', label: 'Food Insecurity', color: 'bg-orange-600', defaultChecked: true },
  { id: 'cholera', label: 'Disease Outbreaks', color: 'bg-red-600', defaultChecked: true },
  { id: 'flooding', label: 'Flood Risk', color: 'bg-blue-500', defaultChecked: true },
  { id: 'drought', label: 'Drought Zones', color: 'bg-amber-600', defaultChecked: true },
  { id: 'teams', label: 'Response Teams', color: 'bg-green-600', defaultChecked: true },
];

// EWAS data layers (NRC EWAS Sudan Pipeline)
const ewasLayers = [
  { id: 'ewas_acled', sourceId: 'acled' as EWASSourceId, label: 'ACLED Conflict', color: 'bg-red-900', defaultChecked: true },
  { id: 'ewas_iom', sourceId: 'iom_dtm' as EWASSourceId, label: 'IOM DTM', color: 'bg-violet-600', defaultChecked: true },
  { id: 'ewas_fewsnet', sourceId: 'fewsnet' as EWASSourceId, label: 'FEWSNET', color: 'bg-orange-500', defaultChecked: true },
  { id: 'ewas_dataminr', sourceId: 'dataminr' as EWASSourceId, label: 'Dataminr', color: 'bg-amber-400', defaultChecked: false },
];

// Generate timeline dates
const generateTimelineDates = () => {
  const dates = [];
  const startDate = new Date(2024, 0, 1);
  const endDate = new Date(2026, 0, 12);

  let currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    dates.push({
      date: new Date(currentDate),
      label: currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      hasAlert: Math.random() > 0.6,
    });
    currentDate.setMonth(currentDate.getMonth() + 1);
  }
  return dates;
};

const timelineDates = generateTimelineDates();

// Fallback token for wireframe demos
const MAPBOX_FALLBACK_TOKEN = 'pk.eyJ1Ijoic2FtZnJvbnMiLCJhIjoiY20wcXprNHFmMDM5dDJpb3A3czEyYXU2OCJ9.z7lv5-q6CZW8Xq59kn1bYA';

export function WireframeCrisisMap() {
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embedded') === 'true';

  // Notify parent window of current page for tooltip coordination
  useEffect(() => {
    if (isEmbedded && window.parent !== window) {
      window.parent.postMessage({ type: 'CLEAR_DEMO_PAGE', page: 'map' }, '*');
    }
  }, [isEmbedded]);

  const [selectedCountry, setSelectedCountry] = useState('Sudan');
  const [selectedRegion, setSelectedRegion] = useState('All Regions');
  const [selectedDate, setSelectedDate] = useState(new Date(2026, 0, 12));
  const [activeLayers, setActiveLayers] = useState<string[]>(['conflict', 'displacement', 'food_insecurity', 'cholera', 'flooding', 'drought', 'teams']);
  const [activeEWASLayers, setActiveEWASLayers] = useState<string[]>(['ewas_acled', 'ewas_iom', 'ewas_fewsnet']);
  const [showEWASLayers, setShowEWASLayers] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState<CrisisMarker | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timelinePosition, setTimelinePosition] = useState(timelineDates.length - 1);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<{ marker: mapboxgl.Marker; type: string; region?: string }[]>([]);
  const ewasMarkersRef = useRef<{ marker: mapboxgl.Marker; layerId: string; region?: string }[]>([]);
  const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN || MAPBOX_FALLBACK_TOKEN;

  const toggleLayer = (layerId: string) => {
    setActiveLayers((prev) =>
      prev.includes(layerId) ? prev.filter((l) => l !== layerId) : [...prev, layerId]
    );
  };

  const toggleEWASLayer = (layerId: string) => {
    setActiveEWASLayers((prev) =>
      prev.includes(layerId) ? prev.filter((l) => l !== layerId) : [...prev, layerId]
    );
  };

  // Add EWAS markers to map
  const addEWASMarkersToMap = (map: mapboxgl.Map) => {
    // Clear existing EWAS markers
    ewasMarkersRef.current.forEach(({ marker }) => marker.remove());
    ewasMarkersRef.current = [];

    const ewasColors: Record<string, string> = {
      acled: '#7F1D1D', // dark red
      acled_cast: '#991B1B',
      iom_dtm: '#7C3AED', // violet
      dtm_flash: '#8B5CF6',
      fewsnet: '#EA580C', // orange
      dataminr: '#F59E0B', // amber
    };

    // Get EWAS records for Sudan
    const ewasRecords = getMockEWASRecords({ admin1: 'Sudan' });

    ewasRecords.forEach((record) => {
      const layerId = record.source === 'acled' || record.source === 'acled_cast' ? 'ewas_acled' :
                      record.source === 'iom_dtm' || record.source === 'dtm_flash' ? 'ewas_iom' :
                      record.source === 'fewsnet' ? 'ewas_fewsnet' :
                      record.source === 'dataminr' ? 'ewas_dataminr' : null;

      if (!layerId) return;

      const color = ewasColors[record.source] || '#6B7280';

      const el = document.createElement('div');
      el.style.cssText = `
        width: 20px;
        height: 20px;
        background: ${color};
        border: 2px solid white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 8px;
        font-weight: 700;
        color: white;
        cursor: pointer;
        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        ${record.severity === 'critical' ? 'animation: ewas-pulse 1.5s infinite;' : ''}
      `;

      // Icon based on source
      const sourceIcons: Record<string, string> = {
        acled: '⚔',
        acled_cast: '⚔',
        iom_dtm: '👥',
        dtm_flash: '⚡',
        fewsnet: '🌾',
        dataminr: '📡',
      };
      el.innerHTML = sourceIcons[record.source] || '●';

      // Create popup
      const popupContent = `
        <div style="padding: 8px; font-family: Inter, sans-serif; min-width: 180px;">
          <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 8px;">
            <span style="background: ${color}; color: white; padding: 2px 6px; font-size: 9px; font-weight: 700; text-transform: uppercase;">
              ${record.sourceName}
            </span>
            <span style="font-size: 10px; color: ${record.severity === 'critical' ? '#DC2626' : record.severity === 'high' ? '#F59E0B' : '#6B7280'}; font-weight: 600; text-transform: uppercase;">
              ${record.severity}
            </span>
          </div>
          <div style="font-weight: 600; font-size: 13px; margin-bottom: 6px;">${record.location.name}</div>
          <div style="font-size: 11px; color: #525252; margin-bottom: 8px;">${record.description || record.variable}</div>
          ${record.value ? `<div style="font-size: 12px; display: flex; justify-content: space-between; padding: 4px 0; border-top: 1px solid #E5E5E5;"><span style="color: #737373;">Value</span><span style="font-weight: 600;">${record.value.toLocaleString()} ${record.unit || ''}</span></div>` : ''}
          <div style="font-size: 10px; color: #9CA3AF; margin-top: 6px;">
            ${new Date(record.date).toLocaleDateString()} • EWAS Sudan
          </div>
        </div>
      `;

      const popup = new mapboxgl.Popup({ offset: 20, closeButton: true, closeOnClick: false })
        .setHTML(popupContent);

      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([record.location.lng, record.location.lat])
        .setPopup(popup)
        .addTo(map);

      ewasMarkersRef.current.push({ marker, layerId, region: record.location.admin1 });
    });
  };

  // Toggle EWAS marker visibility based on active layers
  useEffect(() => {
    ewasMarkersRef.current.forEach(({ marker, layerId, region }) => {
      const el = marker.getElement();
      if (el) {
        const layerActive = showEWASLayers && activeEWASLayers.includes(layerId);
        const regionMatch = selectedRegion === 'All Regions' || region === selectedRegion;
        el.style.display = layerActive && regionMatch ? 'flex' : 'none';
      }
    });
  }, [activeEWASLayers, showEWASLayers, selectedRegion]);

  const getMarkersForCountry = (country: string): CrisisMarker[] => {
    switch (country) {
      case 'Sudan':
        return sudanCrisisMarkers;
      case 'Ethiopia':
        return ethiopiaCrisisMarkers;
      default:
        return sudanCrisisMarkers;
    }
  };

  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
    setSelectedRegion('All Regions');
    setSelectedMarker(null);

    const countryInfo = countryData[country];
    if (mapRef.current && countryInfo) {
      mapRef.current.flyTo({
        center: countryInfo.center,
        zoom: countryInfo.zoom,
        duration: 1500
      });
    }

    // Clear existing markers and add new ones
    markersRef.current.forEach(({ marker }) => marker.remove());
    markersRef.current = [];

    // Clear EWAS markers
    ewasMarkersRef.current.forEach(({ marker }) => marker.remove());
    ewasMarkersRef.current = [];

    if (mapRef.current) {
      addMarkersToMap(mapRef.current, getMarkersForCountry(country));
      // Only add EWAS markers for Sudan (EWAS focuses on Sudan)
      if (country === 'Sudan') {
        addEWASMarkersToMap(mapRef.current);
      }
    }
  };

  const handleRegionChange = (region: string) => {
    setSelectedRegion(region);
    setSelectedMarker(null);

    // Filter markers visibility by region
    markersRef.current.forEach(({ marker, region: markerRegion }) => {
      const el = marker.getElement();
      if (el) {
        if (region === 'All Regions' || markerRegion === region) {
          el.style.display = 'flex';
        } else {
          el.style.display = 'none';
        }
      }
    });

    // Fly to region center if specific region selected
    if (region !== 'All Regions') {
      const markers = getMarkersForCountry(selectedCountry).filter(m => m.region === region);
      if (markers.length > 0 && mapRef.current) {
        const avgLng = markers.reduce((sum, m) => sum + m.coordinates[0], 0) / markers.length;
        const avgLat = markers.reduce((sum, m) => sum + m.coordinates[1], 0) / markers.length;
        mapRef.current.flyTo({
          center: [avgLng, avgLat],
          zoom: 7,
          duration: 1000
        });
      }
    } else if (mapRef.current) {
      const countryInfo = countryData[selectedCountry];
      mapRef.current.flyTo({
        center: countryInfo.center,
        zoom: countryInfo.zoom,
        duration: 1000
      });
    }
  };

  const addMarkersToMap = (map: mapboxgl.Map, markers: CrisisMarker[]) => {
    const colors: Record<string, string> = {
      cholera: '#DC2626',
      flooding: '#3B82F6',
      drought: '#D97706',
      team: '#059669',
      conflict: '#991B1B',
      displacement: '#9333EA',
      food_insecurity: '#EA580C',
    };

    markers.forEach((crisis) => {
      const el = document.createElement('div');
      const color = colors[crisis.type];

      el.style.cssText = `
        width: ${crisis.type === 'team' ? '28px' : '32px'};
        height: ${crisis.type === 'team' ? '28px' : '32px'};
        background: ${crisis.type === 'team' ? color : 'white'};
        border: 3px solid ${color};
        border-radius: ${crisis.type === 'team' ? '50%' : '4px'};
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        font-weight: 700;
        color: ${crisis.type === 'team' ? 'white' : color};
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        position: relative;
        ${crisis.severity === 'critical' ? 'animation: pulse 1.5s infinite;' : ''}
      `;

      // Icon based on type
      const icons: Record<string, string> = {
        conflict: '⚔',
        displacement: '👥',
        food_insecurity: '!',
        cholera: crisis.cases ? String(crisis.cases) : '☣',
        flooding: '💧',
        drought: '☀',
        team: '●',
      };
      el.innerHTML = crisis.cases && crisis.type === 'cholera' ? String(crisis.cases) : icons[crisis.type] || '!';

      // Tooltip on hover
      const tooltip = document.createElement('div');
      tooltip.className = 'marker-tooltip';
      tooltip.style.cssText = `
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        background: white;
        border: 1px solid #e5e7eb;
        padding: 8px 12px;
        white-space: nowrap;
        font-size: 11px;
        font-weight: 500;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        margin-bottom: 8px;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.15s;
        z-index: 100;
      `;
      tooltip.innerHTML = `
        <div style="font-weight: 600; margin-bottom: 2px;">${crisis.name}</div>
        ${crisis.severity ? `<div style="color: ${crisis.severity === 'critical' ? '#DC2626' : crisis.severity === 'high' ? '#F59E0B' : '#6B7280'}; font-size: 10px; text-transform: uppercase;">${crisis.severity} severity</div>` : ''}
        ${crisis.affectedPopulation ? `<div style="color: #6B7280;">~${(crisis.affectedPopulation / 1000).toFixed(0)}k affected</div>` : ''}
        ${crisis.cases ? `<div style="color: #6B7280;">${crisis.cases} cases</div>` : ''}
      `;
      el.appendChild(tooltip);

      el.addEventListener('mouseenter', () => {
        tooltip.style.opacity = '1';
      });
      el.addEventListener('mouseleave', () => {
        tooltip.style.opacity = '0';
      });

      // Create popup with crisis info (fixes marker positioning bug on click)
      const popupContent = crisis.type === 'team'
        ? `
          <div style="padding: 8px; font-family: Inter, sans-serif; min-width: 160px;">
            <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px;">${crisis.name}</div>
            <div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0; border-bottom: 1px solid #E5E5E5;">
              <span style="color: #737373;">Status</span>
              <span style="font-weight: 600; color: #059669;">${crisis.status}</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0; border-bottom: 1px solid #E5E5E5;">
              <span style="color: #737373;">Members</span>
              <span style="font-weight: 600;">${crisis.members}</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0;">
              <span style="color: #737373;">Region</span>
              <span style="font-weight: 600;">${crisis.region}</span>
            </div>
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #E5E5E5; font-size: 11px; color: #6B7280;">
              Click for team details →
            </div>
          </div>
        `
        : `
          <div style="padding: 8px; font-family: Inter, sans-serif; min-width: 180px;">
            <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px;">${crisis.name}</div>
            ${crisis.severity ? `<div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0; border-bottom: 1px solid #E5E5E5;">
              <span style="color: #737373;">Severity</span>
              <span style="font-weight: 600; color: ${crisis.severity === 'critical' ? '#DC2626' : crisis.severity === 'high' ? '#F59E0B' : '#6B7280'}; text-transform: uppercase;">${crisis.severity}</span>
            </div>` : ''}
            ${crisis.affectedPopulation ? `<div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0; border-bottom: 1px solid #E5E5E5;">
              <span style="color: #737373;">Affected</span>
              <span style="font-weight: 600; color: #DC2626;">${(crisis.affectedPopulation / 1000).toFixed(0)}k</span>
            </div>` : ''}
            ${crisis.cases ? `<div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0; border-bottom: 1px solid #E5E5E5;">
              <span style="color: #737373;">Cases</span>
              <span style="font-weight: 600; color: #DC2626;">${crisis.cases}</span>
            </div>` : ''}
            <div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0;">
              <span style="color: #737373;">Region</span>
              <span style="font-weight: 600;">${crisis.region}</span>
            </div>
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #E5E5E5; font-size: 11px; color: #6B7280;">
              Click for full details →
            </div>
          </div>
        `;

      const popup = new mapboxgl.Popup({ offset: 25, closeButton: true, closeOnClick: false })
        .setHTML(popupContent);

      // Handle popup open to update side panel
      popup.on('open', () => {
        setSelectedMarker(crisis);
      });

      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat(crisis.coordinates)
        .setPopup(popup)
        .addTo(map);

      markersRef.current.push({ marker, type: crisis.type, region: crisis.region });
    });
  };

  // Handle timeline playback
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying) {
      interval = setInterval(() => {
        setTimelinePosition(prev => {
          if (prev >= timelineDates.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  // Update date when timeline position changes
  useEffect(() => {
    if (timelineDates[timelinePosition]) {
      setSelectedDate(timelineDates[timelinePosition].date);
    }
  }, [timelinePosition]);

  // Initialize map
  useEffect(() => {
    if (mapRef.current) {
      return;
    }

    const container = document.getElementById('wireframe-crisis-map');
    if (!container) {
      return;
    }

    mapboxgl.accessToken = mapboxToken;

    // Initialize with Sudan center
    const countryInfo = countryData['Sudan'];
    const map = new mapboxgl.Map({
      container: 'wireframe-crisis-map',
      style: 'mapbox://styles/mapbox/light-v11',
      center: countryInfo.center,
      zoom: countryInfo.zoom,
    });

    mapRef.current = map;

    map.on('error', (e) => {
      console.error('Mapbox error:', e);
      setMapError('Failed to load map. Please check your connection.');
    });

    map.on('load', () => {
      addMarkersToMap(map, sudanCrisisMarkers);
      // Add EWAS markers for Sudan
      addEWASMarkersToMap(map);
    });
  }, []);

  // Toggle marker visibility based on active layers
  useEffect(() => {
    markersRef.current.forEach(({ marker, type, region }) => {
      const layerType = type === 'team' ? 'teams' : type;
      const el = marker.getElement();
      if (el) {
        const layerActive = activeLayers.includes(layerType);
        const regionMatch = selectedRegion === 'All Regions' || region === selectedRegion;
        el.style.display = layerActive && regionMatch ? 'flex' : 'none';
      }
    });
  }, [activeLayers, selectedRegion]);

  return (
    <WireframeLayout fullHeight noPadding embedded={isEmbedded}>
      {/* Mobile: min 50vh, Desktop: min-h-[500px] */}
      <div className="flex-1 relative min-h-[50vh] lg:min-h-[500px] flex flex-col">
        {/* Map Controls Header */}
        <div className="absolute top-0 left-0 right-0 z-10 px-2 sm:px-4 py-2 sm:py-4 bg-gradient-to-b from-white via-white/90 to-transparent flex flex-col sm:flex-row justify-between items-start gap-2 pointer-events-none">
          <div data-tutorial="map-filters" className="flex flex-wrap gap-2 sm:gap-3 pointer-events-auto">
            <FilterDropdown
              label="Country"
              value={selectedCountry}
              options={countries}
              onChange={handleCountryChange}
            />
            <FilterDropdown
              label="Region"
              value={selectedRegion}
              options={countryData[selectedCountry]?.regions || ['All Regions']}
              onChange={handleRegionChange}
            />
            <FilterDropdown
              label="Crisis Type"
              value="All Types"
              options={['All Types', 'Conflict', 'Displacement', 'Food Insecurity', 'Disease', 'Flooding']}
              onChange={() => {}}
              className="hidden sm:block"
            />
          </div>
          <div className="flex gap-2 pointer-events-auto">
            <TutorialStartButton tutorialId="crisis-map" variant="icon" />
            <WireframeButton icon={<Search className="w-4 h-4" />}><span className="hidden sm:inline">Search</span></WireframeButton>
            <WireframeButton icon={<Download className="w-4 h-4" />}><span className="hidden sm:inline">Export</span></WireframeButton>
          </div>
        </div>

        {/* Map - Mobile optimized: fills available space with min 40vh, larger on desktop */}
        <div
          ref={mapContainerRef}
          id="wireframe-crisis-map"
          data-tutorial="map-markers"
          className="w-full flex-1 min-h-[40vh] lg:min-h-[400px]"
        />

        {/* Map Error State */}
        {mapError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-center p-8">
              <div className="text-gray-400 text-4xl mb-4">🗺️</div>
              <div className="text-gray-600 font-medium">{mapError}</div>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-black text-white text-sm hover:bg-gray-800"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Layer Controls - Collapsible on mobile, expanded on desktop */}
        <div data-tutorial="data-layers" className="absolute top-20 sm:top-20 left-2 sm:left-4 z-10 bg-white border border-gray-200 w-[160px] sm:w-[200px]">
          <div className="px-3 py-2 sm:py-3 border-b border-gray-200 flex items-center gap-2">
            <Layers className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" />
            <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wide text-gray-500">Layers</span>
          </div>
          <div className="p-2 sm:p-3 space-y-1 sm:space-y-2 max-h-[50vh] overflow-y-auto">
            {/* CLEAR Crisis Layers */}
            <div className="text-[9px] font-bold uppercase tracking-wide text-gray-400 pt-1 pb-0.5">CLEAR Data</div>
            {layers.map((layer) => (
              <label key={layer.id} className="flex items-center gap-1.5 sm:gap-2 py-1 sm:py-1.5 cursor-pointer hover:bg-gray-50 px-1.5 sm:px-2 -mx-1.5 sm:-mx-2">
                <input
                  type="checkbox"
                  checked={activeLayers.includes(layer.id)}
                  onChange={() => toggleLayer(layer.id)}
                  className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-none border-gray-300"
                />
                <span
                  className={cn('w-2 h-2 sm:w-2.5 sm:h-2.5', layer.color)}
                />
                <span className="text-[10px] sm:text-xs text-gray-700 truncate">{layer.label}</span>
              </label>
            ))}

            {/* EWAS Sudan Layers (only show for Sudan) */}
            {selectedCountry === 'Sudan' && (
              <>
                <div className="border-t border-gray-100 mt-2 pt-2">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1">
                      <Database className="w-3 h-3 text-accent-500" />
                      <span className="text-[9px] font-bold uppercase tracking-wide text-accent-600">EWAS Sudan</span>
                    </div>
                    <button
                      onClick={() => setShowEWASLayers(!showEWASLayers)}
                      className={cn(
                        'text-[8px] font-semibold px-1.5 py-0.5',
                        showEWASLayers ? 'bg-accent-500 text-white' : 'bg-gray-200 text-gray-600'
                      )}
                    >
                      {showEWASLayers ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  {showEWASLayers && ewasLayers.map((layer) => (
                    <label key={layer.id} className="flex items-center gap-1.5 sm:gap-2 py-1 sm:py-1.5 cursor-pointer hover:bg-gray-50 px-1.5 sm:px-2 -mx-1.5 sm:-mx-2">
                      <input
                        type="checkbox"
                        checked={activeEWASLayers.includes(layer.id)}
                        onChange={() => toggleEWASLayer(layer.id)}
                        className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-none border-gray-300"
                      />
                      <span
                        className={cn('w-2 h-2 sm:w-2.5 sm:h-2.5', layer.color)}
                      />
                      <span className="text-[10px] sm:text-xs text-gray-700 truncate">{layer.label}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Selected Marker Panel */}
        {selectedMarker && (
          <div className="absolute top-24 sm:top-20 right-2 sm:right-4 left-2 sm:left-auto z-10 bg-white border border-gray-200 w-auto sm:w-[320px]">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <span className="font-semibold text-sm">{selectedMarker.name}</span>
              <button
                onClick={() => setSelectedMarker(null)}
                className="w-6 h-6 flex items-center justify-center hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              {selectedMarker.type !== 'team' ? (
                <>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="text-[11px] text-gray-500 uppercase">Severity</div>
                      <CrisisBadge severity={selectedMarker.severity || 'medium'} />
                    </div>
                    {selectedMarker.affectedPopulation && (
                      <div className="text-right">
                        <div className="text-[11px] text-gray-500 uppercase">Affected</div>
                        <div className="text-lg font-bold text-red-600">
                          {(selectedMarker.affectedPopulation / 1000).toFixed(0)}k
                        </div>
                      </div>
                    )}
                    {selectedMarker.cases && (
                      <div className="text-right">
                        <div className="text-[11px] text-gray-500 uppercase">Cases</div>
                        <div className="text-lg font-bold text-red-600">{selectedMarker.cases}</div>
                      </div>
                    )}
                  </div>
                  {selectedMarker.description && (
                    <div className="text-xs text-gray-600 mb-4 pb-3 border-b border-gray-100">
                      {selectedMarker.description}
                    </div>
                  )}
                  <div className="space-y-2 text-xs mb-4">
                    <div className="flex justify-between py-1.5 border-b border-gray-100">
                      <span className="text-gray-500">Region</span>
                      <span className="font-medium">{selectedMarker.region}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-gray-100">
                      <span className="text-gray-500">Crisis Type</span>
                      <span className="font-medium capitalize">{selectedMarker.type.replace('_', ' ')}</span>
                    </div>
                    <div className="flex justify-between py-1.5">
                      <span className="text-gray-500">Coordinates</span>
                      <span className="font-mono text-[11px]">
                        {selectedMarker.coordinates[1].toFixed(2)}, {selectedMarker.coordinates[0].toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <WireframeButton size="sm" variant="primary" className="flex-1 justify-center">
                      View Details
                    </WireframeButton>
                    <WireframeButton size="sm" className="flex-1 justify-center">
                      Add to Report
                    </WireframeButton>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="text-[11px] text-gray-500 uppercase">Status</div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className={cn(
                          'w-2 h-2',
                          selectedMarker.status === 'Active' ? 'bg-green-600' : 'bg-amber-500'
                        )} />
                        <span className="font-medium text-sm">{selectedMarker.status}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] text-gray-500 uppercase">Members</div>
                      <div className="text-lg font-bold">{selectedMarker.members}</div>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs mb-4">
                    <div className="flex justify-between py-1.5 border-b border-gray-100">
                      <span className="text-gray-500">Team Type</span>
                      <span className="font-medium">{selectedMarker.name.split(' ')[0]}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-gray-100">
                      <span className="text-gray-500">Region</span>
                      <span className="font-medium">{selectedMarker.region}</span>
                    </div>
                    <div className="flex justify-between py-1.5">
                      <span className="text-gray-500">Last Check-in</span>
                      <span className="font-medium">15 min ago</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <WireframeButton size="sm" variant="primary" className="flex-1 justify-center">
                      Contact Team
                    </WireframeButton>
                    <WireframeButton size="sm" className="flex-1 justify-center">
                      View Profile
                    </WireframeButton>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Map Legend - Hidden on very small screens, compact on mobile */}
        <div data-tutorial="map-legend" className="hidden sm:block absolute bottom-24 lg:bottom-24 left-2 sm:left-4 z-10 bg-white border border-gray-200 p-2 sm:p-3 min-w-[120px] sm:min-w-[160px]">
          <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">
            Legend
          </div>
          <div className="space-y-1 sm:space-y-1.5">
            <LegendItem color="bg-red-800" label="Conflict" />
            <LegendItem color="bg-purple-600" label="Displacement" />
            <LegendItem color="bg-orange-600" label="Food Crisis" />
            <LegendItem color="bg-red-600" label="Disease" />
            <LegendItem color="bg-blue-500" label="Flood Risk" />
            <LegendItem color="bg-green-600" label="Teams" />
          </div>
        </div>

        {/* Quick Actions - Compact on mobile */}
        <div data-tutorial="map-actions" className="absolute bottom-24 lg:bottom-24 right-2 sm:right-4 z-10 flex flex-col sm:flex-row gap-1.5 sm:gap-2">
          <WireframeButton icon={<FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4" />} size="sm" className="text-[10px] sm:text-[13px]"><span className="hidden sm:inline">Briefing</span><span className="sm:hidden">Brief</span></WireframeButton>
          <WireframeButton variant="primary" icon={<Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4" />} size="sm" className="text-[10px] sm:text-[13px]"><span className="hidden sm:inline">Alert</span><span className="sm:hidden">Alert</span></WireframeButton>
        </div>

        {/* Timeline at Bottom - Compact on mobile */}
        <div className="bg-white border-t border-gray-200 px-2 sm:px-4 py-1.5 sm:py-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5 sm:gap-4">
            {/* Controls Row */}
            <div className="flex items-center justify-between sm:justify-start gap-1.5 sm:gap-2">
              {/* Play/Pause Controls */}
              <div className="flex items-center gap-0.5 sm:gap-2">
                <button
                  onClick={() => setTimelinePosition(Math.max(0, timelinePosition - 1))}
                  className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-gray-100 border border-gray-200"
                >
                  <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-gray-100 border border-gray-200"
                >
                  {isPlaying ? <Pause className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                </button>
                <button
                  onClick={() => setTimelinePosition(Math.min(timelineDates.length - 1, timelinePosition + 1))}
                  className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-gray-100 border border-gray-200"
                >
                  <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
              </div>

              {/* Current Date Display */}
              <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 min-w-[90px] sm:min-w-[130px]">
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" />
                <span className="text-[10px] sm:text-sm font-medium">
                  {selectedDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </span>
              </div>

              {/* View Mode Toggle - Desktop only */}
              <div className="hidden lg:flex border border-gray-200">
                <button className="px-3 py-1.5 text-xs hover:bg-gray-50 border-r border-gray-200">
                  Single
                </button>
                <button className="px-3 py-1.5 text-xs hover:bg-gray-50 border-r border-gray-200">
                  Compare
                </button>
                <button className="px-3 py-1.5 text-xs bg-black text-white">
                  Timelapse
                </button>
              </div>
            </div>

            {/* Timeline Slider */}
            <div className="flex-1 relative">
              <div className="h-5 sm:h-8 flex items-center">
                <div className="w-full h-1 bg-gray-200 relative">
                  {/* Alert markers on timeline */}
                  {timelineDates.map((item, idx) => (
                    item.hasAlert && (
                      <div
                        key={idx}
                        className="absolute w-1 h-1 sm:w-1.5 sm:h-1.5 bg-red-500 top-1/2 -translate-y-1/2"
                        style={{ left: `${(idx / (timelineDates.length - 1)) * 100}%` }}
                        title={`Alert: ${item.label}`}
                      />
                    )
                  ))}
                  {/* Progress bar */}
                  <div
                    className="absolute h-full bg-black left-0"
                    style={{ width: `${(timelinePosition / (timelineDates.length - 1)) * 100}%` }}
                  />
                  {/* Slider thumb */}
                  <input
                    type="range"
                    min={0}
                    max={timelineDates.length - 1}
                    value={timelinePosition}
                    onChange={(e) => setTimelinePosition(parseInt(e.target.value))}
                    className="absolute inset-0 w-full opacity-0 cursor-pointer"
                  />
                  <div
                    className="absolute w-3 h-3 sm:w-4 sm:h-4 bg-black top-1/2 -translate-y-1/2 -translate-x-1/2 cursor-pointer"
                    style={{ left: `${(timelinePosition / (timelineDates.length - 1)) * 100}%` }}
                  />
                </div>
              </div>
              {/* Timeline labels - fewer on mobile */}
              <div className="flex justify-between mt-0.5 sm:mt-1 text-[8px] sm:text-[10px] text-gray-500">
                <span>'24</span>
                <span className="hidden sm:inline">Jul '24</span>
                <span>'25</span>
                <span className="hidden sm:inline">Jul '25</span>
                <span>'26</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
          50% { box-shadow: 0 0 0 8px rgba(220, 38, 38, 0); }
        }
        @keyframes ewas-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(127, 29, 29, 0.5); }
          50% { box-shadow: 0 0 0 6px rgba(127, 29, 29, 0); }
        }
      `}</style>
    </WireframeLayout>
  );
}

interface FilterDropdownProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  className?: string;
}

function FilterDropdown({ label, value, options, onChange, className }: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white border border-gray-200 px-2 sm:px-4 py-1.5 sm:py-2 min-w-[100px] sm:min-w-[130px] cursor-pointer hover:border-gray-300"
      >
        <div className="text-[8px] sm:text-[10px] text-gray-500 uppercase tracking-wide">{label}</div>
        <div className="text-[11px] sm:text-[13px] font-semibold flex items-center justify-between gap-1 sm:gap-2">
          <span className="truncate">{value}</span>
          <ChevronDown className={cn("w-3 h-3 text-gray-400 transition-transform flex-shrink-0", isOpen && "rotate-180")} />
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 shadow-lg z-50 min-w-[140px] sm:min-w-[180px] max-h-[50vh] sm:max-h-[300px] overflow-y-auto">
          {options.map((option) => (
            <div
              key={option}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              className={cn(
                "px-3 sm:px-4 py-2 text-xs sm:text-sm cursor-pointer hover:bg-gray-50",
                value === option && "bg-gray-100 font-medium"
              )}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px] sm:text-xs">
      <span className={cn('w-2 h-2 sm:w-2.5 sm:h-2.5', color)} />
      <span className="truncate">{label}</span>
    </div>
  );
}

export default WireframeCrisisMap;
